import React, { useLayoutEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { authRoutes, unAuthRoutes } from './utils/routes/path';
import ScrollToTop from './utils/hooks/scrollToTop';
import { LoaderComponent } from './components/spinner/spinner';
import Authenticate from './hooks/AuthenticateRoute';
import { setHeader, setPreviousRoute } from './core/redux/commonSlice';
import { useDispatch, useSelector } from 'react-redux';
import UnAuthRoute from './hooks/UnAuthenticateRoute';
import { Outlet } from 'react-router-dom';
import FooterSection from './pages/Components/footerSection';
import { routes } from './utils/routes/routes';
import { RootState } from './core/redux/store';
import { erorrImage } from './utils/imagepath';
import Header1 from './feature-module/common/header/header-1';
import Header2 from './feature-module/common/header/header-2';
import Header3 from './feature-module/common/header/header-3';
import Header4 from './feature-module/common/header/header-4';
import Header5 from './feature-module/common/header/header-5';
import MenuHeader from './feature-module/common/menuHeader';
import { getData } from './services/api.service';
import { end_points } from './services/endpoints';
import CrispChat from './components/CrispChat';

const ErrorInfo = () => {
  const navigate = useNavigate();

  return (
    <div className="container-error">
      <img src={erorrImage} alt="Error" />
      <button onClick={() => navigate(routes.home)}>Back to Home</button>
    </div>
  );
};

const AppLayout: React.FC = React.memo(() => {
  const dispatch = useDispatch();
  const [isHeaderLoading, setIsHeaderLoading] = useState(true);

  const headerData = useSelector((state: RootState) => state.common.header);
  const [homeTheme, setHomeTheme] = useState(headerData?.template);

  const themeData = useSelector((state: RootState) => state.common.themeJson);

  const getHeader = async () => {
    try {
      const response = await getData(end_points.headers.url);
      dispatch(setHeader(response?.data?.data));
    } catch (error) {
      console.log(error);
    } finally {
      setIsHeaderLoading(false);
    }
  };

  useLayoutEffect(() => {
    getHeader();
  }, []);

  // Update homeTheme when headerData changes
  useLayoutEffect(() => {
    if (headerData?.template) {
      setHomeTheme(headerData.template);
    }
  }, [headerData]);

  const location = useLocation();

  useLayoutEffect(() => {
    if (!headerData) return;

    const siteName = headerData.title || "Madras Masala";

    let path = location.pathname;

    let lastPart = path.split("/").filter(Boolean).pop() || "";

    let pageTitle = lastPart
      .replace(/-/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase());

    if (!pageTitle) pageTitle = "Home";

    document.title = `${pageTitle} | ${siteName}`;

    const favicon = headerData.favicon || "/favicon.png";
    const link = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (link) link.href = favicon;

    const GA_ID = headerData.google_tag_manager?.measurement_id ?? null;
    const GTM_ID = headerData.google_tag_manager?.gtm_container_id ?? null;

    // const replacePlaceholders = (str: string | null): string | null => {
    //   if (!str) return null;

    //   return str
    //     .replace(/{{\s*\?GA4?_?MEAS+UREMENT_ID\s*}}/gi, GA_ID)
    //     .replace(/\{\{\s*\?GA4?_?MEAS+UREMENT_ID\s*\}\}/gi, GA_ID)
    //     .replace(/{{\s*\?GTM_CONTAINER_ID\s*}}/gi, GTM_ID)
    //     .replace(/\{\{\s*\?GTM_CONTAINER_ID\s*\}\}/gi, GTM_ID);
    // };
    const replacePlaceholders = (str: string | null): string | null => {
  if (!str) return null;

  return str
    // 1. Existing replacements
    .replace(/{{\s*\?GA4?_?MEAS+UREMENT_ID\s*}}/gi, GA_ID)
    .replace(/{{\s*\?GTM_CONTAINER_ID\s*}}/gi, GTM_ID)

    // 2. STRIP OUT BROKEN TAGS (The Fix)
    .replace(/@endif/g, "") // Removes the @endif text
    .replace(/{{--.*?--}}/g, "") // Removes the Laravel comments
    
    // 3. FIX THE QUOTE BUG (The primary SyntaxError cause)
    // Changes 'form[data-gtm-form='true']' to 'form[data-gtm-form="true"]'
    .replace(/data-gtm-form='true'/g, 'data-gtm-form="true"');
};


    const headerScriptRaw = replacePlaceholders(headerData.google_tag_manager?.header_script ?? null);
    const bodyScriptRaw = replacePlaceholders(headerData.google_tag_manager?.body_script ?? null);
    const customScriptRaw = replacePlaceholders(headerData.google_tag_manager?.custom_script ?? null);

    const createScriptElement = (node: HTMLElement): HTMLScriptElement | null => {
      const newScript = document.createElement("script");

      for (const attr of Array.from(node.attributes || [])) {
        if (attr.name === "id") continue;
        newScript.setAttribute(attr.name, attr.value);
      }

      if (node.textContent && node.nodeName.toLowerCase() === "script") {
        const inlineScript = node.textContent;
        try {
          // Prevent malformed inline scripts from crashing DOM insertion.
          // eslint-disable-next-line no-new-func
          new Function(inlineScript);
          newScript.textContent = inlineScript;
        } catch (error) {
          console.error("Skipping invalid inline script", error);
          return null;
        }
      }

      return newScript;
    };

    try {
      if (headerData.google_tag_manager?.status === 1 && headerScriptRaw) {

        document.querySelectorAll("[data-gtm-header]").forEach(n => n.remove());

        const temp = document.createElement("div");
        temp.innerHTML = headerScriptRaw;

        const scripts = Array.from(temp.querySelectorAll("script"));

        scripts.forEach((oldScript, i) => {
          const newScript = createScriptElement(oldScript as HTMLElement);
          if (newScript) {
            newScript.setAttribute("data-gtm-header", "true");
            document.head.appendChild(newScript);
          }
        });
      }
    } catch (e) {
      console.error("Header script error", e);
    }

    try {
      if (headerData.google_tag_manager?.status === 1 && bodyScriptRaw) {

        // cleanup old injected body scripts
        document.querySelectorAll("[data-gtm-body-script]").forEach(n => n.remove());

        const temp = document.createElement("div");
        temp.innerHTML = bodyScriptRaw.trim();

        const nodes = Array.from(temp.childNodes);

        let insertPoint = document.body.firstChild;

        nodes.forEach((node, idx) => {
          if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === "script") {
            const newScript = createScriptElement(node as HTMLElement);
            if (newScript) {
              newScript.setAttribute("data-gtm-body-script", "true");
              newScript.setAttribute("data-gtm-index", idx.toString());
              document.body.insertBefore(newScript, insertPoint);
              insertPoint = newScript.nextSibling;
            }
          } else {
            const cloned = node.cloneNode(true) as Node;

            if (cloned.nodeType === Node.ELEMENT_NODE) {
              (cloned as Element).setAttribute("data-gtm-body-script", "true");
              (cloned as Element).setAttribute("data-gtm-index", idx.toString());
            }

            document.body.insertBefore(cloned, insertPoint);
            insertPoint = cloned.nextSibling;
          }
        });
      }
    } catch (e) {
      console.error("Body script error", e);
    }

    try {
      if (customScriptRaw) {
        document.querySelectorAll("[data-custom-script]").forEach(n => n.remove());

        const temp = document.createElement("div");
        temp.innerHTML = customScriptRaw;

        const scripts = Array.from(temp.querySelectorAll("script"));

        if (scripts.length > 0) {
          scripts.forEach((oldScript, index) => {
            const newScript = createScriptElement(oldScript as HTMLElement);
            if (newScript) {
              newScript.setAttribute("data-custom-script", "true");
              document.body.appendChild(newScript);
            }
          });
        } else {
          const wrapper = document.createElement("div");
          wrapper.setAttribute("data-custom-script", "true");
          wrapper.innerHTML = customScriptRaw;
          document.body.appendChild(wrapper);
        }
      }
    } catch (e) {
      console.error("Custom script error", e);
    }

  }, [location.pathname, headerData]);

  useLayoutEffect(() => {
    if (homeTheme === '1') {
      import('./assets/css/style1.css');
    } else if (homeTheme === '2') {
      import('./assets/css/style2.css');
    } else if (homeTheme === '3') {
      import('./assets/css/style3.css');
    } else if (homeTheme === '4') {
      import('./assets/css/style4.css');
    } else if (homeTheme === '5') {
      import('./assets/css/style5.css');
    }
  }, [homeTheme]);

  // Show loader while header data is being fetched
  if (isHeaderLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      {/* <Header /> */}
      {/* <MenuHeader /> */}
      <CrispChat />
      {homeTheme === '1' ? (
        <Header1 themeData={themeData} />
      ) : homeTheme === '2' ? (
        <Header2 themeData={themeData} />
      ) : homeTheme === '3' ? (
        <Header3 themeData={themeData} />
      ) : homeTheme === '4' ? (
        <Header4 themeData={themeData} />
      ) : homeTheme === '5' ? (
        <>
          <Header5 themeData={themeData} />
          <MenuHeader />
        </>
      ) : null}
      <Outlet />
      <FooterSection />
    </>
  );
});

const AppRouter: React.FC = () => {
  const auth = localStorage.getItem('token');
  const RouterContent = React.memo(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState<boolean | null>(
      auth !== '' && auth !== null && auth !== undefined ? true : null
    );

    // const previousRoute = useSelector(
    //   (state: RootState) => state.common.previousRoute
    // );

    const isLoggedIn = useSelector(
      (state: RootState) => state.common.isLoading
    );

    if (location.pathname === '/login') {
      console.log('Authenticated');
      if (authenticated) {
        navigate(routes.customerDashboard, { replace: true });
      }
    }

    useLayoutEffect(() => {
      setIsLoading(true);
      dispatch(setPreviousRoute(location.pathname));

      const checkAuth = async () => {
        const authState = isLoggedIn;
        setAuthenticated(authState);

        setIsLoading(false); // Only hide loader once auth check is complete
      };

      checkAuth();
    }, [location.pathname, dispatch]);

    const renderRoutes = (routeList: any[], isProtected: boolean) =>
      routeList?.map((item) => (
        <Route
          key={`route-${item?.id}`}
          path={item?.path}
          element={
            isProtected ? (
              <Authenticate>{item?.element}</Authenticate>
            ) : (
              <UnAuthRoute>{item?.element}</UnAuthRoute>
            )
          }
        />
      ));

    if (isLoading) {
      return <LoaderComponent />; // Keep showing the loader until auth check completes
    }

    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/" element={<AppLayout />}>
            <>
              {/* <Route
                path="/service/"
                element={<Navigate to={routes.home} replace />}
              />
              <Route path="/" element={<Navigate to={routes.home} replace />} /> */}
            </>
            {renderRoutes(unAuthRoutes, false)}
            {renderRoutes(authRoutes, true)}
          </Route>
          <Route path="*" element={<ErrorInfo />} />
        </Routes>
      </>
    );
  });

  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

export default AppRouter;
